import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card, Col, Form, Row, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import Select from 'react-select';

const WarehouseEdit = () => {
  const navigate = useNavigate();
  const { pointindex } = useParams();
  const { id } = useParams();
  const [branch, setBranch] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  const [warehousename, setwarehousename] = useState("");
  const [warehousetype, setwarehousetype] = useState('');
  const [contactperson, setcontactperson] = useState("");
  const [email, setEmail] = useState("");
  const [mobileno, setmobileno] = useState("");
  const [address, setaddress] = useState("");
  const [zipcode, setzipcode] = useState("");
  const [country, setcountry] = useState(null);
  const [state, setstate] = useState(null);
  const [city, setcity] = useState(null);
  const [countryOptions, setcountryOptions] = useState([]);
  const [stateOptions, setstateOptions] = useState([]);
  const [cityOptions, setcityOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statecode, setstatecode] = useState("");

  // Validation states
  const [errors, setErrors] = useState({
    warehousename: "",
    warehousetype: "",
    branch: "",
    contactperson: "",
    email: "",
    mobileno: "",
    address: "",
    zipcode: "",
    country: "",
    state: "",
    city: ""
  });

  const [touched, setTouched] = useState({
    warehousename: false,
    warehousetype: false,
    branch: false,
    contactperson: false,
    email: false,
    mobileno: false,
    address: false,
    zipcode: false,
    country: false,
    state: false,
    city: false
  });

  // Capitalization functions
  const capitalizeAllWords = (text) => {
    if (!text) return text;
    return text
      .split(' ')
      .map(word => {
        if (word.length === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}warehouse/findById/${id}`, {
          headers: authHeader(),
        });
        const branchData = response.data;
        setstatecode(branchData.statecode);
        setwarehousename(branchData.warehousename);
        setwarehousetype(branchData.warehousetype);
        setBranch({ value: branchData.branch.id, label: branchData.branch.branchname });
        setmobileno(branchData.mobileno);
        setEmail(branchData.email);
        setaddress(branchData.address);
        setzipcode(branchData.zipcode);
        setcontactperson(branchData.contactperson);

        const countryResponse = await axios.get(BASE_URL + 'country/findAllCountry', {
          headers: authHeader(),
        });
        setcountryOptions(countryResponse.data.map(country => ({
          value: country.id,
          label: country.countryname
        })));

        if (branchData.country) {
          const stateResponse = await axios.get(
            BASE_URL + `state/stateByCountry/${branchData.country.id}`,
            { headers: authHeader() }
          );
          setstateOptions(stateResponse.data.map(state => ({
            value: state.id,
            label: state.statename,
            code: state.statecode
          })));
        }

        if (branchData.state) {
          const cityResponse = await axios.get(
            BASE_URL + `city/cityByStateId/${branchData.state.id}`,
            { headers: authHeader() }
          );
          setcityOptions(cityResponse.data.map(city => ({
            value: city.id,
            label: city.cityname
          })));
        }

        setcountry(branchData.country ? {
          value: branchData.country.id,
          label: branchData.country.countryname
        } : null);

        setstate(branchData.state ? {
          value: branchData.state.id,
          label: branchData.state.statename
        } : null);

        setcity(branchData.city ? {
          value: branchData.city.id,
          label: branchData.city.cityname
        } : null);

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch warehouse data. Please try again.',
        });
      }
    };

    fetchWarehouseData();
    getBranchData();
  }, [id]);

  const getBranchData = async () => {
    try {
      const response = await axios.get(BASE_URL + 'branch/findAllBranch', {
        headers: authHeader(),
      });
      setBranchOptions(response.data.map(branch => ({
        value: branch.id,
        label: branch.branchname
      })));
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch branch. Please try again.',
      });
    }
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "warehousename":
        if (!value || !value.trim()) error = "Warehouse Name is required";
        else if (value.trim().length < 3) error = "Warehouse Name must be at least 3 characters";
        else if (value.trim().length > 100) error = "Warehouse Name must be less than 100 characters";
        break;
        
      case "warehousetype":
        if (!value || !value.trim()) error = "Warehouse Type is required";
        break;
        
      case "branch":
        if (!value) error = "Branch is required";
        break;
        
      case "contactperson":
        if (value && value.trim()) {
          if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
            error = "Contact Person should contain only letters and spaces";
          } else if (value.trim().length < 3) {
            error = "Contact Person must be at least 3 characters";
          }
        }
        break;
        
      case "email":
        if (value && value.trim() && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
        
      case "mobileno":
        if (value && value.trim() && !/^[6-9]\d{9}$/.test(value)) {
          error = "Please enter a valid 10-digit mobile number starting with 6-9";
        }
        break;
        
      case "address":
        if (value && value.trim() && value.trim().length < 5) {
          error = "Address must be at least 5 characters if provided";
        }
        break;
        
      case "zipcode":
        if (value && value.trim() && !/^[1-9][0-9]{5}$/.test(value)) {
          error = "Please enter a valid 6-digit pincode";
        }
        break;
        
      case "country":
        if (!value) error = "Country is required";
        break;
        
      case "state":
        if (!value) error = "State is required";
        break;
        
      case "city":
        if (!value) error = "City is required";
        break;
        
      default:
        break;
    }

    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target || {};
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    switch (name) {
      case "warehousename":
        processedValue = capitalizeAllWords(value);
        setwarehousename(processedValue);
        break;
      case "warehousetype":
        setwarehousetype(value);
        break;
      case "contactperson":
        processedValue = capitalizeAllWords(value);
        setcontactperson(processedValue);
        break;
      case "email":
        processedValue = value.toLowerCase();
        setEmail(processedValue);
        break;
      case "mobileno":
        processedValue = value.replace(/\D/g, '').slice(0, 10);
        setmobileno(processedValue);
        break;
      case "address":
        processedValue = capitalizeAllWords(value);
        setaddress(processedValue);
        break;
      case "zipcode":
        processedValue = value.replace(/\D/g, '').slice(0, 6);
        setzipcode(processedValue);
        break;
      default:
        break;
    }

    if (touched[name]) {
      const error = validateField(name, processedValue);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBranchChange = (selectedOption) => {
    setBranch(selectedOption);
    setTouched(prev => ({ ...prev, branch: true }));
    const error = validateField("branch", selectedOption);
    setErrors(prev => ({ ...prev, branch: error }));
  };

  const handleCountryChange = async (selectedOption) => {
    setcountry(selectedOption);
    setstate(null);
    setcity(null);
    setstatecode("");
    setTouched(prev => ({ ...prev, country: true, state: true, city: true }));
    
    if (!selectedOption) {
      setErrors(prev => ({ ...prev, country: "Country is required" }));
      setstateOptions([]);
      setcityOptions([]);
      return;
    }
    
    setErrors(prev => ({ ...prev, country: "" }));

    try {
      const response = await axios.get(
        BASE_URL + `state/stateByCountry/${selectedOption.value}`,
        { headers: authHeader() }
      );
      setstateOptions(response.data.map(state => ({
        value: state.id,
        label: state.statename,
        code: state.statecode
      })));
      setcityOptions([]);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch states. Please try again.',
      });
    }
  };

  const handleStateChange = async (selectedOption) => {
    setstate(selectedOption);
    setcity(null);
    setstatecode(selectedOption?.code || "");
    setTouched(prev => ({ ...prev, state: true, city: true }));
    
    if (!selectedOption) {
      setErrors(prev => ({ ...prev, state: "State is required" }));
      setcityOptions([]);
      return;
    }
    
    setErrors(prev => ({ ...prev, state: "" }));

    try {
      const response = await axios.get(
        BASE_URL + `city/cityByStateId/${selectedOption.value}`,
        { headers: authHeader() }
      );
      setcityOptions(response.data.map(city => ({
        value: city.id,
        label: city.cityname
      })));
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch cities. Please try again.',
      });
    }
  };

  const handleCityChange = (selectedOption) => {
    setcity(selectedOption);
    setTouched(prev => ({ ...prev, city: true }));
    
    if (!selectedOption) {
      setErrors(prev => ({ ...prev, city: "City is required" }));
    } else {
      setErrors(prev => ({ ...prev, city: "" }));
    }
  };

  // Improved validateAllFields function
  const validateAllFields = () => {
    const newErrors = {};
    const errorFields = [];
    let isValid = true;

    // Required field validations
    const requiredFields = [
      { name: "warehousename", value: warehousename, label: "Warehouse Name" },
      { name: "warehousetype", value: warehousetype, label: "Warehouse Type" },
      { name: "branch", value: branch, label: "Branch" },
      { name: "country", value: country, label: "Country" },
      { name: "state", value: state, label: "State" },
      { name: "city", value: city, label: "City" }
    ];

    for (const field of requiredFields) {
      const error = validateField(field.name, field.value);
      if (error) {
        newErrors[field.name] = error;
        errorFields.push(field.label);
        isValid = false;
      }
    }

    // Optional field validations
    const optionalFields = [
      { name: "contactperson", value: contactperson, label: "Contact Person" },
      { name: "email", value: email, label: "Email ID" },
      { name: "mobileno", value: mobileno, label: "Mobile Number" },
      { name: "address", value: address, label: "Address" },
      { name: "zipcode", value: zipcode, label: "Zip Code" }
    ];

    for (const field of optionalFields) {
      if (field.value && field.value.trim()) {
        const error = validateField(field.name, field.value);
        if (error) {
          newErrors[field.name] = error;
          errorFields.push(field.label);
          isValid = false;
        }
      }
    }

    setErrors(prev => ({ ...prev, ...newErrors }));

    // Show detailed error message
    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        html: `<div style="text-align: left;">
          <strong>Please fix the following errors:</strong><br/><br/>
          ${errorFields.map(field => `• ${field}`).join('<br/>')}
        </div>`,
        confirmButtonText: 'OK'
      });
    }

    return isValid;
  };

  const handleSubmit = async () => {
    // Mark all fields as touched
    setTouched({
      warehousename: true,
      warehousetype: true,
      branch: true,
      contactperson: true,
      email: true,
      mobileno: true,
      address: true,
      zipcode: true,
      country: true,
      state: true,
      city: true
    });

    // Validate all fields
    const isFormValid = validateAllFields();
    
    if (!isFormValid) {
      // Scroll to the first error field
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }

    setIsLoading(true);

    const warehouseData = {
      branch: { id: branch.value },
      warehousename: warehousename.trim(),
      warehousetype,
      contactperson: contactperson.trim() || null,
      email: email.toLowerCase().trim() || null,
      mobileno: mobileno.trim() || null,
      address: address.trim() || null,
      zipcode: zipcode.trim() || null,
      country: country ? { id: country.value } : null,
      state: state ? { id: state.value } : null,
      city: city ? { id: city.value } : null,
      statecode,
    };

    try {
      await axios.put(`${BASE_URL}warehouse/update/${id}`, warehouseData, {
        headers: authHeader(),
      });
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Warehouse updated successfully!',
      });
      navigate("/warehouseindex/1");
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data || "Failed to update warehouse",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="Container">
      <Card className="card">
        <Card.Header className="card-header">
          <h6>UPDATE WAREHOUSE DETAILS</h6>
          <Link to={`/warehouseindex/${pointindex}`}>
            <button className="button-70">BACK</button>
          </Link>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <Col md="6">
                <Form.Group controlId="formwarehousename">
                  <Form.Label>Warehouse Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="warehousename"
                    placeholder="Enter warehouse name"
                    value={warehousename}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.warehousename && !!errors.warehousename}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.warehousename}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formwarehousetype">
                  <Form.Label>Warehouse Type <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="warehousetype"
                    value={warehousetype}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.warehousetype && !!errors.warehousetype}
                  >
                    <option value="">Select warehouse Type</option>
                    <option value="Primary">Primary</option>
                    <option value="Secondary">Secondary</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.warehousetype}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="supplier">
                  <Form.Label>Branch <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={branch}
                    onChange={handleBranchChange}
                    onBlur={() => setTouched(prev => ({ ...prev, branch: true }))}
                    options={branchOptions}
                    placeholder="Select branch"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  {touched.branch && errors.branch && (
                    <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {errors.branch}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formcontactperson">
                  <Form.Label>Contact Person</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactperson"
                    value={contactperson}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.contactperson && !!errors.contactperson}
                    placeholder="Enter contact person name (Optional)"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactperson}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Optional: Only letters and spaces</Form.Text>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formemail">
                  <Form.Label>Email ID</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                    placeholder="Enter email address (Optional)"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Optional: Valid email address</Form.Text>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formmobileno">
                  <Form.Label>Phone No.</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobileno"
                    value={mobileno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.mobileno && !!errors.mobileno}
                    maxLength="10"
                    placeholder="Enter 10-digit mobile number"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobileno}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Optional: 10-digit number starting with 6-9</Form.Text>
                </Form.Group>
              </Col>
              <Col md="12">
                <Form.Group controlId="formaddress">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="address"
                    value={address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.address && !!errors.address}
                    placeholder="Enter Address (Optional)"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Optional: At least 5 characters if provided</Form.Text>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formcountry">
                  <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={country}
                    onChange={handleCountryChange}
                    options={countryOptions}
                    placeholder="Select country"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  {touched.country && errors.country && (
                    <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {errors.country}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formstate">
                  <Form.Label>State <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={state}
                    onChange={handleStateChange}
                    options={stateOptions}
                    placeholder="Select state"
                    isDisabled={!country}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  {touched.state && errors.state && (
                    <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {errors.state}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formstatecode">
                  <Form.Label>State Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={statecode}
                    disabled
                    placeholder="Auto-filled"
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formcity">
                  <Form.Label>City <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={city}
                    onChange={handleCityChange}
                    options={cityOptions}
                    placeholder="Select city"
                    isDisabled={!state}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  {touched.city && errors.city && (
                    <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {errors.city}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formzipcode">
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipcode"
                    value={zipcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.zipcode && !!errors.zipcode}
                    maxLength="6"
                    placeholder="Enter 6-digit pincode"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.zipcode}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">Optional: 6-digit pincode</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <button
              className="btn btn-success"
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? <Spinner animation="border" size="sm" /> : "UPDATE"}
            </button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default WarehouseEdit;